import { IAttribute, IModel, IViolation, Collection } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { PbiDataflow, Credential, ModelConstants, format, FabricDataflowLinkedService, 
    IValidationContext, ParameterConfigUtils, ParameterConfig, sanitizeAndFormatString, shellStringResources,
    LinkedService, ParameterValue, ValidationConstants } from 'shell-models-lib';
import { Activity } from './activity';
import { Copier } from 'app/model/base/copier';
import { ComputeType, TraceLevelType } from 'app/shared/constants/dataflow-runtime.options';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import type { ModelService } from 'app/model/services/model.service';
import { isMdfDataflowActivity } from 'app/model/adapters/dataflow/dataflow-workload-payload.util';

export type NotificationOption = 'MailOnCompletion' | 'MailOnFailure' | 'NoNotification';

@Metamodel.model({
    name: ModelConstants.dataflowRefreshActivity,
    display: __IS_FABRIC_DPE__
        ? [stringResources.DataFlowSingleWord, stringResources.DataFlowsSingleWord]
        : [stringResources.DataFlowRefresh, stringResources.DataFlowRefresh],
    referable: true
})
export class DataflowRefreshActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly CREDENTIAL: IAttribute;
    public static readonly DATAFLOWID: IAttribute;
    public static readonly WORKSPACEID: IAttribute;
    public static readonly NOTIFICATIONVALUE: IAttribute;
    public static readonly DATAFLOWTYPE: IAttribute;
    public static readonly COMPUTE_TYPE: IAttribute;
    public static readonly CORE_COUNT: IAttribute;
    public static readonly TRACE_LEVEL: IAttribute;
    public static readonly RUN_CONCURRENTLY: IAttribute;
    public static readonly CONTINUE_ON_ERROR: IAttribute;
    public static readonly STAGING_STORAGE_PATH: IAttribute;
    public static readonly STAGING_LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly CUSTOMIZED_CHECKPOINT_KEY: IAttribute;
    
    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        reference: true,
        type: FabricDataflowLinkedService.MODEL
    })
    public linkedService?: FabricDataflowLinkedService;

    @Metamodel.attribute({
        display: stringResources.DataFlow,
        reference: true,
        type: PbiDataflow.MODEL
    })
    public dataflow?: PbiDataflow;

    @Metamodel.attribute({
        display: stringResources.ModelName_Credential,
        reference: true,
        association: 'CREDENTIAL',
        templatable: false
    })
    public credential?: Credential;

    @Metamodel.attribute({
        reference: true,
        association: 'DATAFLOWID',
        constraint: {
            validator: DataflowRefreshActivity.validateDataflowId
        },
        templatable: true
    })
    public dataflowId?: string;

    @Metamodel.attribute({
        reference: true,
        association: 'WORKSPACEID',
        constraint: {
            validator: DataflowRefreshActivity.validateWorkspaceId
        },
        templatable: true
    })
    public workspaceId?: string;

    @Metamodel.attribute({
        reference: true,
        association: 'NOTIFICATIONVALUE',
        templatable: true
    })
    public notificationValue: string = 'NoNotification';

    @Metamodel.attribute({
        reference: true,
        association: 'DATAFLOWTYPE',
        templatable: true
    })
    public dataflowType?: string;

    @Metamodel.attribute({
        display: stringResources.Parameter,
        constraint: {
            validator: DataflowRefreshActivity.validateParameters
        }
    })
    public parameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: stringResources.DataFlowComputeTypeLabel,
        constraint: { required: false },
        templatable: true,
        association: 'COMPUTE_TYPE'
    })
    public computeType?: ComputeType = 'General';

    @Metamodel.attribute({
        display: stringResources.DataFlowCoreCountLabel,
        constraint: {
            required: false,
            minimum: 8,
            whole: true
        },
        templatable: true,
        association: 'CORE_COUNT'
    })
    public coreCount?: number = 8;

    @Metamodel.attribute({
        display: stringResources.SsisActivityLoggingLevelLabel,
        constraint: {
            required: false,
            validator: DataflowRefreshActivity.validateTraceLevel
        },
        templatable: true,
        association: 'TRACE_LEVEL'
    })
    public traceLevel?: TraceLevelType = 'Fine';

    @Metamodel.attribute({
        display: stringResources.DataFlowRunConcurrenlty,
        templatable: true,
        association: 'RUN_CONCURRENTLY'
    })
    public runConcurrently?: boolean;

    @Metamodel.attribute({
        display: stringResources.DataFlowContinueOnError,
        templatable: true,
        association: 'CONTINUE_ON_ERROR'
    })
    public continueOnError?: boolean;

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingLinkedService,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: DataflowRefreshActivity.validateStagingLinkedService
        }
    })
    public stagingLinkedService?: LinkedService;

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingPath,
        constraint: {
            validator: DataflowRefreshActivity.validateStagingPath
        },
        association: 'STAGING_STORAGE_PATH'
    })
    public stagingStoragePath?: string;

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: { required: false },
        association: 'STAGING_LINKED_SERVICE_PARAMETERS'
    })
    public stagingLinkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.CustomizedCheckpointKey,
        constraint: {
            required: false,
            validator: DataflowRefreshActivity.validateCustomizedCheckpointKey
        },
        templatable: true,
        association: 'CUSTOMIZED_CHECKPOINT_KEY'
    })
    public customizedCheckpointKey?: string;

    /**
     * Native Dataflow Gen2 only.
     * Name of the MDF query (from the mashup document) selected to drive Spark execution.
     * Mutually exclusive with `executeMashupQueriesOnly = true`.
     */
    @Metamodel.attribute({
        reference: true,
        templatable: false
    })
    public sparkTransformName?: string;

    /**
     * Native Dataflow Gen2 only.
     * When true, the activity ignores any MDF queries in the document and runs only the
     * Power Query (M) queries. Set automatically when the document contains zero MDF
     * queries, or when the user picks "Run all M queries" in the dropdown.
     */
    @Metamodel.attribute({
        reference: true,
        templatable: false
    })
    public executeMashupQueriesOnly?: boolean;

    public toggleUsingCustomizedCheckpointKey: boolean = false;
    public alreadyUsingDefaultCheckpointKey: boolean = false;

    private static validateWorkspaceId(_dataflowRefreshActivity?: DataflowRefreshActivity, workspaceId?: string): IViolation[] | undefined {
        if (__IS_FABRIC_DPE__ && !workspaceId) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.WorkspaceIdLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateDataflowId(_dataflowRefreshActivity?: DataflowRefreshActivity, dataflowId?: string): IViolation[] | undefined {
        if (__IS_FABRIC_DPE__ && !dataflowId) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.DataFlowSingleWord)
                }
            ];
        }
        return undefined;
    }

    /* 
    // We need this when connection becomes mandatory

    private static async validateConnection(_dataflowRefreshActivity?: DataflowRefreshActivity, connection?: string, _violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const useTokenServiceInDataflowActivity = (
            await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService')
                ).UseTokenServiceInDataFlowActivity;
        if (GlobalVariables.useDataflowConnection  
            && useTokenServiceInDataflowActivity 
            && _dataflowRefreshActivity?.dataflowType === 'DataflowFabric'
            && __IS_FABRIC_DPE__ 
            && !connection) {
            return [
                {
                    message: () => format(
                        stringResources.RequiredTemplateWhen,
                        stringResources.ConnectionLabel,
                        stringResources.DataFlowSingleWord,
                        stringResources.DataFlowTypeFabric)
                }
            ];
        }
        return undefined;
    }
    */

    private static async validateParameters(_dataflowRefreshActivity?: DataflowRefreshActivity, _parameters?: ParameterConfig[], _violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        let violations: IViolation[];
        if (__IS_FABRIC_DPE__ 
            && (_dataflowRefreshActivity?.dataflowType === 'DataflowFabric' || _dataflowRefreshActivity?.dataflowType === ModelConstants.mappingDataflowfabricPipelineDataflowType)
            ) {
                const parameterValidator = isMdfDataflowActivity(_dataflowRefreshActivity)
                    ? ParameterConfigUtils.createParameterConfigurationValidator(false, null, true)
                    : ParameterConfigUtils.createParameterConfigurationValidator(true, null, false);
            violations = parameterValidator(_dataflowRefreshActivity, _parameters, _violation);
            if (!violations) {
                violations = [];
            }
            _parameters?.forEach((parameter) => parameter && DataflowRefreshActivity.validateParameterValue(violations, parameter, parameter.value));
        }

        return violations?.length > 0 ? violations : undefined;
    }

    private static validateParameterValue(violations: IViolation[], parameter: ParameterConfig, value: string): void {
            if (parameter && parameter.required) {
                if (!parameter.value && ParameterConfig.VALUE.templateValue(parameter) === undefined) {
                    violations.push({
                        message: () => sanitizeAndFormatString(shellStringResources.Activity_Error_Configuration_InvalidValue, parameter.name)
                    });
                }
            }
            if (parameter && value) {
                const parameterType = parameter.type ? parameter.type.toLowerCase() : '';
                switch (parameterType) {
                    case 'int':
                        if (!Number.isInteger(Number(value))) {
                            violations.push({
                                message: () => sanitizeAndFormatString(shellStringResources.Trigger_Parameterization_ValidIntegerErrorMessage, parameter.name)
                            });
                        }
    
                        break;
                    case 'float':
                        if (isNaN(Number(value))) {
                            violations.push({
                                message: () => sanitizeAndFormatString(shellStringResources.Trigger_Parameterization_ValidFloatErrorMessage, parameter.name)
                            });
                        }
    
                        break;
                    case 'bool':
                        if (String(value) !== 'true' && String(value) !== 'false') {
                            violations.push({
                                message: () => sanitizeAndFormatString(shellStringResources.Trigger_Parameterization_ValidBooleanErrorMessage, parameter.name)
                            });
                        }
    
                        break;
                    case 'array':
                        try {
                            const defaultArray = JSON.parse(String(value));
    
                            if (!Array.isArray(defaultArray)) {
                                violations.push({
                                    message: () => sanitizeAndFormatString(shellStringResources.Trigger_Parameterization_ValidArrayErrorMessage, parameter.name)
                                });
                            }
                        } catch (error) {
                            violations.push({
                                message: () => sanitizeAndFormatString(shellStringResources.Trigger_Parameterization_ValidArrayErrorMessage, parameter.name)
                            });
                        }
    
                        break;
                    default:
                        break;
                }
            }
        }

    private static validateTraceLevel(dataflowActivity: DataflowRefreshActivity): IViolation[] {
        if (dataflowActivity && dataflowActivity.traceLevel
            && isMdfDataflowActivity(dataflowActivity)
            && !ValueAdapter.isTemplated(dataflowActivity, DataflowRefreshActivity.TRACE_LEVEL)) {
            const validLevels: TraceLevelType[] = ['Fine', 'Coarse', 'None'];
            if (!validLevels.includes(dataflowActivity.traceLevel)) {
                return [{
                    context: dataflowActivity,
                    message: () => format(stringResources.RequiredTemplate, stringResources.SsisActivityLoggingLevelLabel)
                }];
            }
        }
        return [];
    }

    private static validateStagingLinkedService(_dataflowActivity: DataflowRefreshActivity): IViolation[] | undefined {
        return undefined;
    }

    private static validateStagingPath(dataflowActivity: DataflowRefreshActivity): IViolation[] | undefined {
        if (dataflowActivity?.stagingLinkedService && !dataflowActivity.stagingStoragePath
            && isMdfDataflowActivity(dataflowActivity)) {
            return [{
                context: dataflowActivity,
                message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, 
                    stringResources.DataFlowSingleWord,
                    dataflowActivity.name ? '\'' + dataflowActivity.name + '\'' : '', 
                    stringResources.Dataflow_StagingPath)
            }];
        }
        return undefined;
    }

    private static async validateCustomizedCheckpointKey(dataflowActivity: DataflowRefreshActivity, _value: string, _violation: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        const checkpointName = dataflowActivity.customizedCheckpointKey;
        const violations: IViolation[] = [];
        if (!dataflowActivity.toggleUsingCustomizedCheckpointKey) {
            return undefined;
        }

        if (ValueAdapter.isTemplated(dataflowActivity, DataflowRefreshActivity.CUSTOMIZED_CHECKPOINT_KEY)) {
            return undefined;
        }

        if (stringIsNullOrEmpty(checkpointName)) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => format(stringResources.Validator_NameRequired, stringResources.CustomizedCheckpointKey)
            });
            return violations;
        }

        if (checkpointName.length > ValidationConstants.maxActivityNameLength) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => format(stringResources.Validator_NameMaxLength,
                    stringResources.CustomizedCheckpointKey,
                    ValidationConstants.maxActivityNameLength.toString())
            });
        }

        const noSpaceRegex = ValidationConstants.strictlyNoSpaceNameValidator;
        if (!checkpointName.match(noSpaceRegex)) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => stringResources.CustomizedCheckpointKey_NoSpaces
            });
        }

        // Lazy imports to avoid circular dependency at module load time
        const { getAllExistingCheckpoint } = await import('app/authoring/shared/authoring.utils');
        const modelService = await getServiceInstance<ModelService>(context.contextData, 'ModelService');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return await getAllExistingCheckpoint(dataflowActivity as any, { modelService }).then((allExistingCheckpoint) => {
            const set = new Set(allExistingCheckpoint);
            if (set.has(dataflowActivity.customizedCheckpointKey)) {
                violations.push({
                    context: dataflowActivity.customizedCheckpointKey,
                    message: () => format(stringResources.DuplicateCheckpointKey_Error, stringResources.CustomizedCheckpointKey)
                });
            }
            return violations.length <= 0 ? undefined : violations;
        }).catch(() => {
            // If getAllExistingCheckpoint fails, still return any violations collected before the call
            return violations.length <= 0 ? undefined : violations;
        });
    }

    public onActivityCloned(clonedActivity: DataflowRefreshActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as FabricDataflowLinkedService;
            }
        }
    }
}
